import Alert from "@/components/alert/Alert";
import FormModal from "@/components/modals/FormModal";
import { useAppSelector } from "@/hooks/hook";
import { GET, POST } from "@/services/api";
import { API_ROUTES } from "@/services/environment";
import { Button } from "@thdinc/hpf-shared";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import Select, { StylesConfig } from "react-select";
import { BusinessOption, CashflowSource } from "../interface";

interface CashflowModalProps {
    isOpen: boolean;
    onClose: () => void;
    businessId: string;
    businesses: BusinessOption[];
    onSuccess: () => void;
}

interface CashflowFormValues {
    amount: number;
    source: CashflowSource | "";
    description: string;
}

interface CreateBulkCashflowResult {
    business_id: string;
    success: boolean;
    message?: string;
}

const multiSelectStyles: StylesConfig<BusinessOption, true> = {
    control: (provided, state) => ({
        ...provided,
        backgroundColor: "#f7f8fa",
        borderRadius: "1.5rem",
        minHeight: "49px",
        paddingLeft: "8px",
        border: "none",
        boxShadow: state.menuIsOpen ? "0 0 0 1px #FF5100" : "none",
        "&:hover": { border: "none" },
    }),
    indicatorSeparator: () => ({ display: "none" }),
    menu: (provided) => ({ ...provided, zIndex: 9999 }),
    menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
    multiValue: (provided) => ({
        ...provided,
        backgroundColor: "white",
        borderRadius: "9999px",
        padding: "0 2px",
    }),
};

const CashflowModal: React.FC<CashflowModalProps> = ({ isOpen, onClose, businessId, businesses, onSuccess }) => {
    const { refreshToken } = useAppSelector((store) => store.user);
    const { createBulkCashflowApi, getStripeBalanceApi } = API_ROUTES;
    const [selectAll, setSelectAll] = useState<boolean>(false);
    const [selectedBusinesses, setSelectedBusinesses] = useState<BusinessOption[]>([]);
    const [businessSelectionError, setBusinessSelectionError] = useState<string>("");
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [stripeBalance, setStripeBalance] = useState<{ available: number; currency: string } | null>(null);
    const [isStripeBalanceLoading, setIsStripeBalanceLoading] = useState<boolean>(false);
    const [stripeBalanceError, setStripeBalanceError] = useState<string>("");
    const {
        register,
        handleSubmit,
        reset,
        trigger,
        formState: { errors },
    } = useForm<CashflowFormValues>({ mode: "onChange" });

    useEffect(() => {
        if (isOpen) {
            const current = businesses.find((b) => b.id === businessId);
            setSelectedBusinesses(current ? [current] : []);
            setSelectAll(false);
            setBusinessSelectionError("");
        }
    }, [isOpen, businessId, businesses]);

    useEffect(() => {
        if (!isOpen) return;

        (async () => {
            try {
                setIsStripeBalanceLoading(true);
                setStripeBalanceError("");
                const result = await GET(getStripeBalanceApi, refreshToken);
                setStripeBalance(result?.data?.record ?? null);
            } catch (error) {
                console.log("get stripe balance error:", error);
                setStripeBalanceError("Unable to load Stripe balance");
            } finally {
                setIsStripeBalanceLoading(false);
            }
        })();
    }, [isOpen, getStripeBalanceApi, refreshToken]);

    useEffect(() => {
        trigger("amount");
    }, [selectAll, selectedBusinesses, trigger]);

    const closeAndReset = () => {
        reset();
        onClose();
    };

    const onSubmit = async (data: CashflowFormValues) => {
        const targetBusinesses = selectAll ? businesses : selectedBusinesses;

        if (targetBusinesses.length === 0) {
            setBusinessSelectionError("Select at least one business");
            return;
        }
        setBusinessSelectionError("");

        try {
            setIsSubmitting(true);
            const response = await POST(createBulkCashflowApi, {
                business_ids: targetBusinesses.map((b) => b.id),
                amount: Number(data.amount),
                source: data.source,
                description: data.description,
            });

            const results: CreateBulkCashflowResult[] = response?.data?.results ?? [];
            const failed = results.filter((r) => !r.success);

            if (failed.length === 0) {
                Alert({
                    message: `Cashflow recorded successfully for ${targetBusinesses.length} business${targetBusinesses.length > 1 ? "es" : ""
                        }`,
                });
            } else {
                const nameById = new Map(businesses.map((b) => [b.id, b.name]));
                const failedNames = failed.map((f) => nameById.get(f.business_id) ?? f.business_id).join(", ");
                const successCount = targetBusinesses.length - failed.length;
                Alert({
                    type: "error",
                    message:
                        successCount > 0
                            ? `Cashflow recorded for ${successCount} business(es). Failed for: ${failedNames}`
                            : `Unable to record cashflow for: ${failedNames}`,
                });
            }

            closeAndReset();
            onSuccess();
        } catch (error: any) {
            Alert({
                type: "error",
                message: error?.response?.data?.message ?? "Unable to record cashflow",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <FormModal
            isOpen={isOpen}
            onClose={closeAndReset}
            modalTitle="Cashflow"
            width="lg:w-2/6 md:w-3/6 w-full"
            disableOutsideClick
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="mb-4">
                    <p className="font-semibold text-xs mb-2 ml-4">Current Stripe Balance</p>
                    {isStripeBalanceLoading ? (
                        <p className="px-4 text-sm text-gray-400">Fetching current Stripe balance...</p>
                    ) : stripeBalanceError ? (
                        <p className="px-4 text-sm text-red-500">{stripeBalanceError}</p>
                    ) : stripeBalance ? (
                        <p className="px-4 text-sm text-gray-800 font-semibold">
                            {stripeBalance.available.toLocaleString(undefined, {
                                style: "currency",
                                currency: stripeBalance.currency,
                            })}
                        </p>
                    ) : null}
                </div>

                <div className="mb-4">
                    <p className="font-semibold text-xs mb-2 ml-4">Businesses</p>
                    <label className="flex items-center gap-2 mb-2 ml-4 text-sm text-gray-600 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={selectAll}
                            onChange={(e) => setSelectAll(e.target.checked)}
                            className="accent-primary-red w-4 h-4"
                        />
                        Apply to all businesses
                    </label>
                    {!selectAll && (
                        <Select<BusinessOption, true>
                            isMulti
                            isClearable={false}
                            options={businesses}
                            value={selectedBusinesses}
                            onChange={(selected) => setSelectedBusinesses(selected ? [...selected] : [])}
                            placeholder="Select businesses"
                            getOptionLabel={(option) => option.name}
                            getOptionValue={(option) => option.id}
                            styles={multiSelectStyles}
                            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                            menuPosition="fixed"
                        />
                    )}
                    {businessSelectionError && (
                        <p className="text-red-500 text-xs mb-2 mt-1 ml-4">{businessSelectionError}</p>
                    )}
                </div>

                <div className="mb-4">
                    <p className="font-semibold text-xs mb-2 ml-4">Amount</p>
                    <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                        <input
                            type="number"
                            min={0.01}
                            step={0.01}
                            className="w-full pl-7 pr-4 py-3 bg-p-gray rounded-full focus:outline-none focus:ring-1 focus:ring-primary-red text-gray-600"
                            placeholder="0.00"
                            {...register("amount", {
                                required: "Amount is required",
                                valueAsNumber: true,
                                min: { value: 0.01, message: "Amount must be greater than 0" },
                                validate: (value) => {
                                    if (!stripeBalance) return true;
                                    const targetCount = selectAll ? businesses.length : selectedBusinesses.length;
                                    const totalNeeded = value * Math.max(targetCount, 1);
                                    return totalNeeded <= stripeBalance.available
                                        ? true
                                        : "Total amount for the selected business(es) cannot exceed the current Stripe balance";
                                },
                            })}
                        />
                    </div>
                    <p className="text-red-500 text-xs mb-2 mt-1 ml-4">{errors?.amount?.message}</p>
                </div>

                <div className="mb-4">
                    <p className="font-semibold text-xs mb-2 ml-4">Source</p>
                    <select
                        defaultValue=""
                        className="w-full appearance-none px-4 py-3 bg-p-gray rounded-full focus:outline-none focus:ring-1 focus:ring-primary-red text-gray-600"
                        {...register("source", { required: "Source is required" })}
                    >
                        <option value="" disabled>
                            Select source
                        </option>
                        <option value="giveaway">Giveaway</option>
                        <option value="adjustment">Adjustment</option>
                    </select>
                    <p className="text-red-500 text-xs mb-2 mt-1 ml-4">{errors?.source?.message}</p>
                </div>

                <div className="mb-4">
                    <p className="font-semibold text-xs mb-2 ml-4">Description</p>
                    <textarea
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-3 bg-p-gray rounded-2xl focus:outline-none focus:ring-1 focus:ring-primary-red text-gray-600 resize-none"
                        placeholder="Reason for this cashflow entry"
                        {...register("description", { required: "Description is required" })}
                    />
                    <p className="text-red-500 text-xs mb-2 mt-1 ml-4">{errors?.description?.message}</p>
                </div>

                <div className="text-end">
                    <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="text-center px-6 my-2 rounded-full py-2 bg-primary-red hover:bg-primary-red-button-hover text-white"
                    >
                        {isSubmitting ? "Saving..." : "Save"}
                    </Button>
                </div>
            </form>
        </FormModal>
    );
};

export default CashflowModal;
